import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Agency } from '../types';
import authService from '../services/authService';
import employeeAuthService from '../services/employeeAuthService';
import agencyService from '../services/agencyService';
import NotificationService from '../services/notificationService';
import { useOnboardingStore } from './onboardingStore';
import { useProfilePictureStore } from './profilePictureStore';

export interface EmployeeUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: string;
  department: string;
  permissions: any;
  avatar?: string;
  mustChangePassword: boolean;
  agency: {
    id: string;
    businessName: string;
    displayName: string;
    slug: string;
    status: string;
    timezone?: {
      name?: string;
      offsetMinutes?: number;
    };
  };
}

interface AuthState {
  user: Agency | null;
  employee: EmployeeUser | null;
  // The employee's agency (an employee session has no `user`)
  agencyProfile: Agency | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userType: 'agency' | 'employee' | null;
  
  // Actions
  setUser: (user: Agency) => void;
  setEmployee: (employee: EmployeeUser) => void;
  setToken: (token: string) => void;
  login: (user: Agency, token: string) => void;
  loginEmployee: (employee: EmployeeUser, token: string) => void;
  logout: () => void;
  updateUser: (updates: Partial<Agency>) => void;
  initializeAuth: () => void;
  loadAgencyProfile: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      employee: null,
      agencyProfile: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      userType: null,

      setUser: (user) => set({ user, isAuthenticated: true, userType: 'agency' }),
      
      setEmployee: (employee) => set({ employee, isAuthenticated: true, userType: 'employee' }),
      
      setToken: (token) => set({ token }),
      
      login: (user, token) => {
        // Clear any existing employee session
        localStorage.removeItem('employee_token');
        localStorage.removeItem('employee_user');
        
        localStorage.setItem('agency_token', token);
        localStorage.setItem('agency_user', JSON.stringify(user));
        localStorage.setItem('user_type', 'agency');
        set({ 
          user, 
          token, 
          isAuthenticated: true,
          isLoading: false,
          userType: 'agency',
          employee: null,
          agencyProfile: null
        });
      },

      loginEmployee: (employee, token) => {
        // Clear any existing agency session
        localStorage.removeItem('agency_token');
        localStorage.removeItem('agency_user');
        
        localStorage.setItem('employee_token', token);
        localStorage.setItem('employee_user', JSON.stringify(employee));
        localStorage.setItem('user_type', 'employee');
        set({
          employee,
          token,
          isAuthenticated: true,
          isLoading: false,
          userType: 'employee',
          user: null,
          agencyProfile: null
        });
        get().loadAgencyProfile();
      },
      
      logout: () => {
        const userType = localStorage.getItem('user_type');
        const agencyUserRaw = localStorage.getItem('agency_user');
        const employeeUserRaw = localStorage.getItem('employee_user');

        try {
          const agencyUser = agencyUserRaw ? JSON.parse(agencyUserRaw) : null;
          const employeeUser = employeeUserRaw ? JSON.parse(employeeUserRaw) : null;
          if (agencyUser?._id) {
            sessionStorage.removeItem(`password_change_prompted_${agencyUser._id}`);
          }
          if (employeeUser?.id) {
            sessionStorage.removeItem(`password_change_prompted_${employeeUser.id}`);
          }
        } catch {
          // Ignore malformed session data during logout cleanup
        }
        
        if (userType === 'employee') {
          employeeAuthService.logout();
        } else {
          authService.logout();
        }
        
        // Clear ALL auth-related localStorage items
        localStorage.removeItem('agency_token');
        localStorage.removeItem('agency_user');
        localStorage.removeItem('employee_token');
        localStorage.removeItem('employee_user');
        localStorage.removeItem('user_type');
        localStorage.removeItem('is_demo_account');
        
        // Clear onboarding data on logout
        const { clearOnboardingData } = useOnboardingStore.getState();
        clearOnboardingData();
        
        // Clear profile picture cache on logout
        const { clearProfilePicture } = useProfilePictureStore.getState();
        clearProfilePicture();
        
        set({ 
          user: null,
          employee: null, 
          agencyProfile: null,
          token: null, 
          isAuthenticated: false,
          isLoading: false,
          userType: null
        });
      },
      
      updateUser: (updates) => set((state) => {
        const previousStatus = state.user?.status;
        const newUser = state.user ? { ...state.user, ...updates } : null;
        
        // Check for status changes and create notifications
        if (newUser && previousStatus && newUser.status && previousStatus !== newUser.status) {
          NotificationService.checkStatusChanges(newUser.status, previousStatus);
        }
        
        // Also update the persistent storage for current user
        if (newUser && state.userType === 'agency') {
          authService.updateCurrentUser(newUser);
        }
        
        return { user: newUser };
      }),
      
      initializeAuth: () => {
        const userType = localStorage.getItem('user_type');
        
        if (userType === 'employee') {
          const token = employeeAuthService.getToken();
          const employee = employeeAuthService.getCurrentEmployee();
          
          if (token && employee) {
            set({ 
              employee,
              token, 
              isAuthenticated: true,
              isLoading: false,
              userType: 'employee'
            });
            get().loadAgencyProfile();
            employeeAuthService.refreshAgencyTimezone().then((updated) => {
              if (updated && get().userType === 'employee' && get().employee?.id === updated.id) set({ employee: updated as EmployeeUser });
            });
          } else {
            set({ isLoading: false });
          }
        } else {
          const token = authService.getToken();
          const user = authService.getCurrentUser();
          
          if (token && user) {
            set({ 
              user, 
              token, 
              isAuthenticated: true,
              isLoading: false,
              userType: 'agency'
            });
          } else {
            set({ isLoading: false });
          }
        }
      },

      loadAgencyProfile: async () => {
        if (get().userType !== 'employee') return;
        try {
          const result = await agencyService.getProfile();
          if (result.success && result.data && get().userType === 'employee') {
            set({ agencyProfile: result.data });
          }
        } catch (error) {
          console.error('Failed to load agency profile for employee:', error);
        }
      },
    }),
    {
      name: 'agency-auth-storage',
      partialize: (state) => ({
        user: state.user,
        employee: state.employee,
        agencyProfile: state.agencyProfile,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        userType: state.userType,
      }),
    }
  )
);
